import type { AyzeoPromptPlatformRun, Platform, SectionFourPromptInterpretation, SectionFourResultValidity } from "./domain";
import { isAssessableReview, isExcludedReview, isUnavailableRun, modelDisplayName } from "./section4-models";
import { legacyVisibilityOutcome } from "./section4-suggestions";

const excludedEntities=/^(Google|Google Search|Google Maps|Google Business Profile|Google AI Overviews?|ChatGPT|Gemini|OpenAI|Perplexity|Claude|Copilot|Bing)$/i;
const unique=(values:string[])=>[...new Set(values.map(value=>value.trim()).filter(Boolean))];

function decisionFingerprint(runs:AyzeoPromptPlatformRun[],reviews:SectionFourResultValidity[]){
  const byRun=new Map(reviews.map(item=>[item.sourceRunId,item]));
  return runs.map(run=>{
    const review=byRun.get(run.id);
    return [run.id,run.payloadChecksum,review?.updatedAt,review?.reviewState,review?.assessability,review?.visibilityOutcome,review?.intentMatch,review?.concern].join("|");
  }).join("::");
}

export function createSectionFourPromptInterpretation(
  prompt:{id:string;order:number;wording:string},
  runs:AyzeoPromptPlatformRun[],
  generatedAt=new Date().toISOString(),
  reviews:SectionFourResultValidity[]=[],
  platforms:Platform[]=[],
):SectionFourPromptInterpretation{
  const reviewByRun=new Map(reviews.map(item=>[item.sourceRunId,item]));
  const assessable=runs.filter(run=>isAssessableReview(reviewByRun.get(run.id))&&!isUnavailableRun(run,reviewByRun.get(run.id)));
  const excluded=runs.filter(run=>isExcludedReview(reviewByRun.get(run.id)));
  const unavailable=runs.filter(run=>isUnavailableRun(run,reviewByRun.get(run.id)));
  const pending=runs.filter(run=>!isUnavailableRun(run,reviewByRun.get(run.id))&&!isAssessableReview(reviewByRun.get(run.id))&&!isExcludedReview(reviewByRun.get(run.id)));
  const outcomes=assessable.map(run=>legacyVisibilityOutcome(reviewByRun.get(run.id))).filter(Boolean);
  const appeared=outcomes.length?outcomes.some(outcome=>outcome!=="Absent"):null;
  const cited=assessable.length?assessable.some(run=>(run.citationCount??0)>0):null;
  const mentioned=assessable.length?assessable.some(run=>(run.mentionCount??0)>0||run.brandMentioned===true):null;
  const sources=unique(assessable.flatMap(run=>(run.citations??[]).map(item=>item.domain||item.url)));
  const competitors=unique(assessable.flatMap(run=>run.mentionedBrands)).filter(name=>!excludedEntities.test(name));
  const modelFacts=runs.map(run=>{
    const review=reviewByRun.get(run.id),name=modelDisplayName(run,platforms);
    if(isUnavailableRun(run,review))return`${name}: no result returned.`;
    if(isExcludedReview(review))return`${name} is excluded from commercial visibility judgement${review?.unsuitableReason?` because it was marked ${review.unsuitableReason.toLowerCase()}`:""}.`;
    if(!isAssessableReview(review))return`${name}: human judgement is incomplete.`;
    const outcome=legacyVisibilityOutcome(review);
    const fact=outcome?outcome.toLowerCase():run.brandMentioned===true?"present; visibility outcome still requires review":run.brandMentioned===false?"absent; visibility outcome still requires review":"visibility outcome still requires review";
    return`${name}: ${fact}${(run.citationCount??0)>0?", cited":""}${(run.mentionCount??0)>0||run.brandMentioned===true?", mentioned":""}.`;
  });
  const recommendationAssessment=outcomes.length
    ? `The assessable executions record ${unique(outcomes as string[]).map(item=>item.toLowerCase()).join(" and ")}.`
    : "No current execution has both been confirmed as assessable and given a visibility outcome.";
  const intents=unique(assessable.map(run=>reviewByRun.get(run.id)?.intentMatch??""));
  const intentAssessment=intents.length?`Recorded customer-intent judgements: ${intents.join(", ")}.`:"Customer-intent fit still requires human judgement.";
  const humanCommercial=unique(assessable.map(run=>reviewByRun.get(run.id)?.commercialSignificance??""));
  const commercialSignificance=humanCommercial.join(" ");
  const incompleteCoverage=unavailable.length>0;
  const incompleteReview=pending.length>0;
  const limitations=[
    "The connector supplies the latest available run per returned model rather than complete execution history.",
    ...(excluded.length?[`${excluded.length} execution${excluded.length===1?" was":"s were"} excluded by exact-run review and are not visibility failures.`]:[]),
    ...(unavailable.length?[`${unavailable.length} execution${unavailable.length===1?" was":"s were"} unavailable; model coverage is incomplete.`]:[]),
    ...(pending.length?[`${pending.length} returned execution${pending.length===1?" still requires":"s still require"} human judgement.`]:[]),
  ];
  return{
    promptSnapshotId:prompt.id,
    generatedAt,
    provider:"Local evidence interpretation",
    text:[
      modelFacts.join(" "),
      recommendationAssessment,
      intentAssessment,
      commercialSignificance,
      incompleteCoverage?"The returned model comparison is incomplete.":"",
      incompleteReview||excluded.length?"The cross-model comparison is incomplete.":"",
    ].filter(Boolean).join("\n\n"),
    appeared,
    cited,
    mentioned,
    recommendationAssessment,
    intentAssessment,
    concerns:unique(assessable.map(run=>reviewByRun.get(run.id)?.concern??"").filter(value=>value&&value!=="No material concern"&&value!=="No material issue")),
    competitors:competitors.slice(0,12),
    influentialSources:sources.slice(0,12),
    commercialSignificance,
    limitations,
    sourceRunIds:assessable.map(run=>run.id),
    sourceDecisionFingerprint:decisionFingerprint(runs,reviews),
  };
}
